/** Angular imports */
import { Component, effect, input, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

/** Component imports */
import {
    Checkbox,
    DateRangePicker,
    Input,
    Timepicker,
    ToggleButton,
} from '@ntv360/component-pantry';
import { AccordionComponent, TabsComponent as SharedTabComponent } from '@shared';

/** Local imports */
import { TabsComponent } from '@features/playlists/components/single-playlist/shared/tabs/tabs.component';
import { PlaylistSettingsData } from '@features/playlists/interfaces';

@Component({
    selector: 'app-playlist-settings-content',
    imports: [
        TabsComponent,
        Input,
        ReactiveFormsModule,
        ToggleButton,
        SharedTabComponent,
        DateRangePicker,
        Timepicker,
        Checkbox,
    ],
    templateUrl: './playlist-settings-content.component.html',
    styleUrl: './playlist-settings-content.component.scss',
})
export class PlaylistSettingsContentComponent implements OnInit {
    /** Default static values for playlist settings */
    public static readonly DEFAULT_VALUES = {
        START_TIME: '00:00:00',
        END_TIME: '23:59:59',
        IS_FULLSCREEN: true,
        DAYS_OF_WEEK: [1, 2, 3, 4, 5, 6, 0],
        DAY_MAP: {
            Mon: 1,
            Tue: 2,
            Wed: 3,
            Thu: 4,
            Fri: 5,
            Sat: 6,
            Sun: 0,
        } as Record<string, number>,
    };

    /** Controls the visibility of fullscreen checkbox */
    public showForTM = input<boolean>(false);

    /** Controls the visibility of playlist tabs */
    public showPlaylistTabs = input<boolean>(true);

    /** The visible tabs for this component */
    public visibleTabs = input<('basics' | 'play-where' | 'content-schedule')[]>([
        'basics',
        'play-where',
        'content-schedule',
    ]);

    /** Initial settings data */
    public initialData = input<PlaylistSettingsData | null>(null);

    /** Emits when any settings change */
    public settingsChange = output<PlaylistSettingsData>();

    /** Content duration form control */
    protected contentDurationFC = new FormControl<number>(20, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(5), Validators.max(3600)],
    });

    /** Whether to set the content to fullscreen */
    protected setFullscreen = signal<boolean>(false);

    /** Current main tab */
    protected currentTab = signal<number>(0);

    /** Current sub tab for schedule */
    protected currentSubTab = signal<number>(0);

    // Schedule signals
    protected dateRange = signal<{ start: string | Date | null; end: string | Date | null }>({
        start: null,
        end: null,
    });
    protected playTimeStartFC = new FormControl<string | null>(null, [Validators.required]);
    protected playTimeEndFC = new FormControl<string | null>(null, [Validators.required]);
    protected is24Hours = signal<boolean>(false);
    protected playAllDays = signal<boolean>(true);
    protected selectedDays = signal<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);

    protected readonly WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    constructor() {
        effect(() => {
            this.emitChanges();
        });

        this.contentDurationFC.valueChanges.subscribe(() => {
            this.emitChanges();
        });
        this.playTimeStartFC.valueChanges.subscribe(() => {
            this.emitChanges();
        });
        this.playTimeEndFC.valueChanges.subscribe(() => {
            this.emitChanges();
        });
    }

    /**
     * Sets the initial values of the form controls and signals based on the input data
     */
    ngOnInit(): void {
        const data = this.initialData();

        if (this.showForTM()) this.setFullscreen.set(true);

        if (data) {
            this.contentDurationFC.setValue(data.duration, { emitEvent: false });
            this.setFullscreen.set(data.setFullscreen);
            this.dateRange.set(data?.schedule?.dateRange ?? { start: null, end: null });
            this.playTimeStartFC.setValue(data?.schedule?.playTime?.start ?? null, {
                emitEvent: false,
            });
            this.playTimeEndFC.setValue(data?.schedule?.playTime?.end ?? null, {
                emitEvent: false,
            });
            this.is24Hours.set(data?.schedule?.playTime?.is24hours ?? false);
            this.selectedDays.set(data?.schedule?.daysToPlay?.selectedDays ?? []);
            this.playAllDays.set(data?.schedule?.daysToPlay?.selectedDays?.length === 7);
        }
    }

    /**
     * Gets the default start ISO datetime (now)
     */
    public static get defaultStartDatetime(): string {
        return new Date().toISOString();
    }

    /**
     * Gets the default end ISO datetime (5 years from now)
     */
    public static get defaultEndDatetime(): string {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 5);
        return d.toISOString();
    }

    /**
     * Formats a 12h time string (e.g., '12:00 PM') to a 24h format (e.g., '12:00:00')
     * @param time The time string to format
     * @param defaultTime The default value to return if time is invalid
     */
    public static formatTime(time: string | null, defaultTime: string): string {
        if (!time) return defaultTime;
        const parts = time.split(' ');
        if (parts.length < 2) return time.includes(':') ? `${time}:00` : defaultTime;

        const [timePart, modifier] = parts;
        let [hours, minutes] = timePart.split(':');
        let h = parseInt(hours, 10);
        if (modifier === 'AM' && h === 12) h = 0;
        if (modifier === 'PM' && h !== 12) h += 12;
        return `${String(h).padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
    }

    /** Mapped tabs based on input */
    protected get tabs() {
        const allTabs = [
            { id: 0, key: 'basics', label: 'Basics', iconKey: 'settings' },
            { id: 1, key: 'play-where', label: 'Play Where', iconKey: 'paper-plane' },
            { id: 2, key: 'content-schedule', label: 'Content Schedule', iconKey: 'clock' },
        ];
        return allTabs.filter((t) => this.visibleTabs().includes(t.key as any));
    }

    /**
     * Gets the list of sub-tabs for the content schedule tab
     * @returns An array of sub-tab objects
     */
    protected get subTabs() {
        return [
            { id: '0', label: 'Default Play', iconKey: 'settings' },
            { id: '1', label: 'Custom Schedule', iconKey: 'paper-plane' },
            { id: '2', label: 'Do Not Play', iconKey: 'clock' },
        ];
    }

    /**
     * Emits the aggregated playlist settings data to the parent component
     */
    protected emitChanges(): void {
        const days = this.playAllDays() ? [...this.WEEK_DAYS] : this.selectedDays();

        const data: PlaylistSettingsData = {
            duration: this.contentDurationFC.value,
            setFullscreen: this.setFullscreen(),
            licenses: [], // TODO: extract selected licenses
            schedule: {
                dateRange: this.dateRange(),
                playTime: {
                    start: this.playTimeStartFC.value,
                    end: this.playTimeEndFC.value,
                    is24hours: this.is24Hours(),
                },
                daysToPlay: {
                    selectedDays: days,
                    playAllDays: this.playAllDays(),
                },
            },
        };
        this.settingsChange.emit(data);
    }

    /**
     * Handles the click event of a tab
     * @param tabId The ID of the tab that was clicked
     */
    protected onTabClick(tabId: number): void {
        this.currentTab.set(tabId);
    }

    /**
     * Prevents non-numeric characters from being typed in numeric inputs.
     * @param event The keyboard event
     */
    protected onKeydown(event: KeyboardEvent): void {
        const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'];
        if (allowedKeys.includes(event.key)) return;

        // Allow only numbers 0-9
        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    /**
     * Placeholder method for handling blur events on content duration input.
     * Resets the input to the default value (20) if it is empty or less than 5 seconds
     */
    protected onBlur(): void {
        const val = this.contentDurationFC.value;
        if (!val || val.toString() === '') {
            this.contentDurationFC.setValue(20);
        } else if (val < 5) {
            this.contentDurationFC.setValue(5);
        } else if (val > 3600) {
            this.contentDurationFC.setValue(3600);
        }
    }

    /**
     * Gets the error message for content duration form control
     * @returns The error message or an empty string
     */
    protected getErrorMessage(): string {
        const errors = this.contentDurationFC.errors;
        if (!this.contentDurationFC.touched) {
            return '';
        }
        if (errors?.['required']) {
            return 'Duration is required. Default is 20 seconds.';
        }
        if (errors?.['min']) {
            return 'Content duration must be at least 5 seconds';
        }
        if (errors?.['max']) {
            return 'Content duration cannot exceed 3600 seconds (1 hour)';
        }
        return '';
    }

    /**
     * Gets the error message for timepicker controls
     * @param control The form control to check
     * @returns The error message or an empty string
     */
    protected getTimeErrorMessage(control: FormControl): string {
        if (!control.touched || !control.invalid) {
            return '';
        }
        if (control.errors?.['required']) {
            return 'Time is required';
        }
        return '';
    }

    /**
     * Toggles the selection state of a specific day.
     *
     * Adds the day to the selection if it is not currently selected, or removes it
     * if it is. Automatically updates the `playAllDays` flag to true when all
     * 7 days are selected.
     *
     * @param day - The identifier of the day to toggle (e.g., 'Monday', 'Tue').
     */
    protected toggleDay(day: string): void {
        const current = this.selectedDays();
        if (current.includes(day)) {
            this.selectedDays.set(current.filter((d) => d !== day));
        } else {
            this.selectedDays.set([...current, day]);
        }

        if (this.selectedDays().length === 7) {
            this.playAllDays.set(true);
        } else {
            this.playAllDays.set(false);
        }
    }

    /**
     * Toggles the selection of all available days of the week.
     *
     * Updates the `playAllDays` state and either selects all days or clears
     * the selection entirely based on the provided checkbox state. Finally,
     * emits the updated changes to any listeners.
     *
     * @param checked - A boolean indicating whether to select all days (`true`) or deselect all (`false`).
     */
    protected togglePlayAllDays(checked: boolean): void {
        this.playAllDays.set(checked);
        if (checked) {
            this.selectedDays.set([...this.WEEK_DAYS]);
        } else {
            this.selectedDays.set([]);
        }
        this.emitChanges();
    }

    /**
     * Handles updates to the selected date range.
     *
     * Updates the internal `dateRange` signal with the new start and end values,
     * and immediately emits the updated state to any listeners.
     *
     * @param dates - An object containing the boundaries of the selected date range.
     * @param dates.start - The starting date of the range (can be a string, Date object, or null).
     * @param dates.end - The ending date of the range (can be a string, Date object, or null).
     */
    protected onDatesChange(dates: {
        start: string | Date | null;
        end: string | Date | null;
    }): void {
        this.dateRange.set(dates);
        this.emitChanges();
    }
}
